import React, { useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FileText, Download, Calendar, Package, DollarSign, Wrench, RefreshCw } from 'lucide-react';

const PDFReportsPage = () => {
  const [generating, setGenerating] = useState('');
  const [monthlyForm, setMonthlyForm] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });

  const handleGenerateMonthly = async () => {
    try {
      setGenerating('monthly');
      const response = await api.get('/pdf-reports/monthly-maintenance', {
        params: monthlyForm,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `raport_mentenanta_${monthlyForm.year}_${monthlyForm.month.toString().padStart(2, '0')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Raport PDF generat!');
    } catch (error) {
      toast.error('Eroare la generare raport');
    } finally {
      setGenerating('');
    }
  };

  const handleGenerateInventory = async () => {
    try {
      setGenerating('inventory');
      const response = await api.get('/pdf-reports/inventory-value', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `raport_valoare_stoc_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Raport PDF generat!');
    } catch (error) {
      toast.error('Eroare la generare raport');
    } finally {
      setGenerating('');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Rapoarte PDF Customizabile</h1>
        <p className="text-slate-600 mt-1">Genereaza rapoarte profesionale branded cu logo si grafice</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Monthly Maintenance Report */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Raport Lunar Mentenanta
            </CardTitle>
            <CardDescription>Rezumat executiv, tichete, revizii, costuri</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>An</Label>
                <Input
                  type="number"
                  value={monthlyForm.year}
                  onChange={(e) => setMonthlyForm({...monthlyForm, year: parseInt(e.target.value)})}
                  min="2020"
                  max="2030"
                />
              </div>
              <div className="space-y-2">
                <Label>Luna</Label>
                <Select
                  value={monthlyForm.month.toString()}
                  onValueChange={(v) => setMonthlyForm({...monthlyForm, month: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(12)].map((_, i) => (
                      <SelectItem key={i+1} value={(i+1).toString()}>
                        {new Date(2000, i).toLocaleString('ro-RO', { month: 'long' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-700">Include:</p>
              <ul className="text-xs text-blue-600 mt-1 space-y-1">
                <li>• Rezumat executiv cu KPIs</li>
                <li>• Top 5 echipamente problematice</li>
                <li>• Lista completa tichete + revizii</li>
                <li>• Breakdown costuri</li>
              </ul>
            </div>
            
            <Button 
              onClick={handleGenerateMonthly}
              disabled={generating === 'monthly'}
              className="w-full btn-primary"
            >
              {generating === 'monthly' ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Genereaza...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" />Genereaza PDF</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Inventory Value Report */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-green-600" />
              Raport Valoare Stoc
            </CardTitle>
            <CardDescription>Inventar complet cu valori calculate</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-sm text-green-700">Include:</p>
              <ul className="text-xs text-green-600 mt-1 space-y-1">
                <li>• Valoare totala stoc (RON)</li>
                <li>• Top 30 articole dupa valoare</li>
                <li>• Cantitate + Pret unitar</li>
                <li>• Calculat automat din sistem</li>
              </ul>
            </div>
            
            <Button 
              onClick={handleGenerateInventory}
              disabled={generating === 'inventory'}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {generating === 'inventory' ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Genereaza...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" />Genereaza PDF</>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900">Despre Rapoartele PDF</p>
              <p className="text-blue-700 mt-1">
                Toate rapoartele sunt generate cu logo MAINTIX, header branded, formatare profesionala
                si sunt gata pentru prezentari management sau arhivare oficiala.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PDFReportsPage;